import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import DataTable from "@/components/shared/DataTable";
import SeverityBadge from "@/components/shared/SeverityBadge";
import StatusBadge from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api, { extractApiError } from "@/lib/api";
import { toast } from "sonner";
import { Eye, Send } from "lucide-react";
import VehicleProfileDialog from "@/components/shared/VehicleProfileDialog";
import LocationLabel from "@/components/shared/LocationLabel";

export default function IncidentsPage() {
  const [open, setOpen] = useState(null);
  const [profileVehicleId, setProfileVehicleId] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [incidentsList, setIncidentsList] = useState([]);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  const handleOpenProfile = (vehicleId) => {
    setProfileVehicleId(vehicleId);
    setProfileOpen(true);
  };

  const handleViewIncident = async (incidentOrId) => {
    const id = incidentOrId?.id || incidentOrId;
    if (!id) return;

    // If caller passed an object, open it immediately while we fetch full details
    if (typeof incidentOrId === "object") setOpen(incidentOrId);
    setDialogLoading(true);
    try {
      const resp = await api.get(`/incidents/${id}`);
      const data = resp?.data?.data || resp?.data || resp;
      setOpen(data || null);
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to load incident details.");
      // if we had an object, keep it open; otherwise close dialog
      if (typeof incidentOrId !== "object") setOpen(null);
    } finally {
      setDialogLoading(false);
    }
  };

  function formatDateTime(value) {
    if (!value) return "-";
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      return d.toLocaleString();
    } catch {
      return value;
    }
  }

  React.useEffect(() => {
    const fetchIncidents = async () => {
      setLoading(true);
      setError("");
      try {
        const resp = await api.get("/incidents", { params: { page: 1, page_size: 50 } });
        const rows = resp?.data?.data || resp?.data || [];
        setIncidentsList(Array.isArray(rows) ? rows : []);
      } catch (err) {
        const { message } = extractApiError(err);
        setError(message || "Failed to load incidents.");
      } finally {
        setLoading(false);
      }
    };

    fetchIncidents();
    // If navigated with a selectedId in state, open that incident
    if (location?.state?.selectedId) {
      // open and then clear location state to avoid reopening
      handleViewIncident(String(location.state.selectedId));
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []);

  return (
    <div className="space-y-6" data-testid="incidents-page">
      <PageHeader title="Incidents" subtitle="Cross-service incidents — dispatch and override controls." />

      <VehicleProfileDialog 
        open={profileOpen} 
        onOpenChange={setProfileOpen} 
        vehicleId={profileVehicleId} 
      />

      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="active">Active</TabsTrigger>
          <TabsTrigger value="closed">Closed</TabsTrigger>
        </TabsList>

        {['all', 'active', 'closed'].map((tab) => {
          const list = (incidentsList || []).filter((i) =>
            tab === 'all' ? true : tab === 'closed' ? i.status === 'CLOSED' || i.status === 'closed' : i.status !== 'CLOSED' && i.status !== 'closed',
          );
          return (
            <TabsContent key={tab} value={tab} className="mt-5">
              <SectionCard padded={false}>
                <DataTable
                  data={list}
                  onRowClick={(i) => handleViewIncident(i)}
                  loading={loading}
                  emptyState={error ? <div className="px-4 py-3 text-sm text-rose-600">{error}</div> : undefined}
                  columns={[
                    {
                      key: "driver", label: "Driver",
                      cellClassName: "font-mono text-xs font-semibold text-slate-900",
                      render: (i) => (i.driver?.name || "-"),
                    },
                    { key: "type", label: "Type", cellClassName: "text-slate-700", render: (i) => i.incident_type || i.type || "-" },
                    {
                      key: "vehicle", label: "Vehicle",
                      render: (i) => {
                        const reg = i.vehicles && i.vehicles.length ? i.vehicles[0].registration_number : null;
                        const vehicleId = i.vehicles && i.vehicles.length ? i.vehicles[0].vehicle_id || i.vehicles[0].id : null;
                        return reg ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenProfile(vehicleId);
                            }}
                            className="font-mono text-xs text-blue-600 hover:underline font-bold"
                          >
                            {reg}
                          </button>
                        ) : "-";
                      },
                    },
                    {
                      key: "location", label: "Location",
                      cellClassName: "max-w-[260px] truncate text-slate-700",
                      render: (i) => (
                        <div>
                          <LocationLabel locationValue={i.location} />
                          <div className="text-xs text-slate-500">{formatDateTime(i.event_time || i.eventTime || i.created_at)}</div>
                        </div>
                      ),
                    },
                    { key: "severity", label: "Severity", render: (i) => <SeverityBadge severity={i.severity} /> },
                    { key: "status", label: "Status", render: (i) => <StatusBadge status={i.status} /> },
                    {
                      key: "actions", label: "Actions", className: "text-right", cellClassName: "text-right",
                      render: (i) => (
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleViewIncident(i)} data-testid={`view-${i.id}`}>
                            <Eye size={13} className="mr-1" /> View
                          </Button>
                          {/* <Button
                            size="sm"
                            className="bg-slate-900 text-white hover:bg-slate-800"
                            onClick={() => toast.success(`Dispatched all services for ${i.id}`)}
                            data-testid={`dispatch-${i.id}`}
                          >
                            <Send size={13} className="mr-1" /> Dispatch
                          </Button> */}
                        </div>
                      ),
                    },
                  ]}
                />
              </SectionCard>
            </TabsContent>
          );
        })}
      </Tabs>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="sm:max-w-lg">
          {dialogLoading ? (
            <SectionCard>
              <div className="flex items-center justify-center py-8 text-slate-500">Loading incident…</div>
            </SectionCard>
          ) : open && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">{open.incident_type || open.type || "Incident"}</DialogTitle>
                <DialogDescription>{formatDateTime(open.event_time) || ""}</DialogDescription>
              </DialogHeader>

              <div className="space-y-4 text-sm">
                <div className="flex flex-wrap items-center gap-3">
                  <SeverityBadge severity={open.severity} />
                  <StatusBadge status={open.status} />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="rounded-lg border border-slate-100 bg-white p-3">
                    <p className="text-xs font-semibold text-slate-500">Location</p>
                    {open.location ? (
                      <div className="mt-1 text-slate-700">
                        <LocationLabel locationValue={open.location} fallbackClassName="mt-0.5" />
                      </div>
                    ) : (
                      <div className="mt-1 text-slate-600">Unknown</div>
                    )}

                    <div className="mt-3 text-xs text-slate-500">
                      {/* <div>Created: {formatDateTime(open.created_at)}</div>
                      <div>Updated: {formatDateTime(open.updated_at)}</div> */}
                      <div>Resolved: {open.resolved_at ? formatDateTime(open.resolved_at) : "—"}</div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-slate-100 bg-white p-3">
                    <p className="text-xs font-semibold text-slate-500">Driver</p>
                    {open.driver ? (
                      <div className="mt-1 text-slate-700">
                        <div className="font-semibold">{open.driver.name || "—"}</div>
                        <div className="text-xs text-slate-500">{open.driver.phone || "—"}</div>
                        {/* <div className="text-xs text-slate-500">Source: {open.driver.source || "—"}</div> */}
                      </div>
                    ) : (
                      <div className="mt-1 text-slate-600">No driver info</div>
                    )}
                  </div>
                </div>

                <div className="rounded-lg border border-slate-100 bg-white p-3">
                  <p className="text-xs font-semibold text-slate-500">Vehicles</p>
                  {Array.isArray(open.vehicles) && open.vehicles.length > 0 ? (
                    <div className="mt-2 space-y-2">
                      {open.vehicles.map((v) => (
                        <div key={v.vehicle_id || v.registration_number} className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2">
                          <div>
                            <div className="font-semibold text-slate-700">{v.registration_number || v.vehicle_id}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-2 text-xs text-slate-500">No vehicles associated</div>
                  )}
                </div>

                <div className="rounded-lg border border-slate-100 bg-white p-3">
                  <p className="text-xs font-semibold text-slate-500">Metadata</p>
                  {open.metadata && Object.keys(open.metadata).length > 0 ? (
                    <div className="mt-2 text-xs text-slate-700">
                      {Object.entries(open.metadata).map(([k, v]) => (
                        <div key={k} className="flex justify-between py-0.5">
                          <span className="text-slate-500">{k}</span>
                          <span className="font-medium">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-2 text-xs text-slate-500">No metadata</div>
                  )}
                </div>

                <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-3 text-xs">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-slate-500">Service statuses</p>
                  <div className="grid grid-cols-1 gap-2">
                    {Object.entries(open.service_statuses || {}).length === 0 ? (
                      <div className="text-xs text-slate-500">No service statuses</div>
                    ) : (
                      Object.entries(open.service_statuses || {}).map(([k, v]) => (
                        <div key={k} className="flex items-center justify-between rounded-md bg-white px-2 py-1.5">
                          <div>
                            <div className="capitalize text-slate-600">{k}</div>
                            <div className="text-xs text-slate-500">{v.note || ""}</div>
                          </div>
                          <StatusBadge status={v.status} />
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {open.description || "" ? (
                  <div className="rounded-lg border border-slate-100 bg-white p-3">
                    <p className="text-xs font-semibold text-slate-500">Description</p>
                    <div className="mt-1 text-slate-700 text-sm">{open.description}</div>
                  </div>
                ) : null}

                {/* <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => toast.message("Override applied") }>
                    Override
                  </Button>
                  <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={() => toast.success("Reassigned successfully") }>
                    Reassign
                  </Button>
                </div> */}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}